"use client";

import type { ReactNode } from "react";
import Link from "next/link";

import { LegalFooter } from "@/components/layout/legal-footer";
import { useAuth } from "@/lib/auth/context";

/**
 * Shell for public-facing pages (currently /legal/*). These pages are
 * accessible to BOTH unauthenticated visitors (prospects, regulators,
 * banking vendor-risk reviewers) and authenticated users (a GA opening
 * a doc from the admin console, a CA reading the ToS during onboarding).
 * The header switches between "Sign in" and "Back to platform" based on
 * the in-memory auth state so a signed-in viewer doesn't get pushed
 * through a redundant login.
 */
export default function PublicLayout({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  // Pure platform admins (no company association) home is /admin/companies;
  // anyone else with a session goes back to /dashboard.
  const platformHref =
    user?.isGlobalAdmin && !user?.companyId ? "/admin/companies" : "/dashboard";

  return (
    <div className="min-h-screen bg-bg text-text-primary">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex max-w-240 items-center justify-between px-6 py-4">
          <Link
            href="/"
            className="text-sm font-semibold text-text-primary hover:text-accent"
          >
            Market Intelligence
          </Link>
          <nav className="flex items-center gap-4 text-sm text-text-secondary">
            <Link href="/legal/changelog" className="hover:text-text-primary">
              Changelog
            </Link>
            {isAuthenticated ? (
              <Link href={platformHref} className="hover:text-text-primary">
                Back to platform
              </Link>
            ) : (
              <Link href="/login" className="hover:text-text-primary">
                Sign in
              </Link>
            )}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-240 px-6 py-10">{children}</main>
      <LegalFooter />
    </div>
  );
}
